import { useEffect, useState } from 'react'
import { ArrowRight, Building2, CalendarDays, ClipboardList, Users } from 'lucide-react'
import { apiGet } from '../admin/primitives.js'
import { Link, navigate } from '../router.js'
import { useMe } from '../shell/AppShell.js'
import { useMenuLien } from '../layout/menu-tuiles.js'
import './rh.css'

/**
 * CENTRE RH (Lot 2.5 — PRODUCT RESET, reconstruit de zéro, §11-16).
 *
 * Un cockpit HUMAIN : l'équipe, la semaine, ce qui demande attention.
 * INTERDITS ici (§6-§7) : provider de planning, contrôle d'accès physique,
 * comptes fournisseurs, rapprochements d'identités, états de synchronisation —
 * tout cela vit dans Administration → Intégrations. Zéro KPI décoratif : une
 * brique n'apparaît que si sa donnée existe réellement (§14).
 */

interface Center {
  people: { active: number; onboarding: number; pending: number; leaving: number; without_position: number }
  org: { teams: number; positions: number; functions: number; locations: number }
  planning: { week_start: string; shifts: number; persons: number; tonight: number; published_version: number | null }
  attention: { key: string; label: string; count: number; route: string }[]
}

function frWeek(ymd: string): string {
  return new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', timeZone: 'UTC' })
}

export function RhHubPage() {
  const [d, setD] = useState<Center | null>(null)
  const me = useMe()
  const menuLien = useMenuLien()
  useEffect(() => {
    apiGet<Center>('/api/v1/hr/center').then((r) => { if ('people' in r) setD(r) }).catch(() => undefined)
  }, [])

  const hour = new Date().getHours()
  const salut = hour >= 18 || hour < 4 ? 'Bonsoir' : 'Bonjour'
  const prenom = me?.first_name ?? me?.display_name?.split(/\s+/)[0] ?? ''

  return (
    <main className="lc-page lc-page--management lc-stagger hub-page">
      <header className="hub-hero">
        <p className="hub-hero__kicker">Ressources humaines</p>
        <h1 className="lc-title">{salut}{prenom ? ` ${prenom}` : ''}. Voici l’état de l’équipe.</h1>
      </header>

      {!d ? <p className="lc-hint">Chargement…</p> : (
        <div className="center-grid">
          {/* ─── CETTE SEMAINE — la grande surface (§15) ─── */}
          {/* Une adresse stable : le planning s'ouvre de lui-même sur la semaine en cours,
              et un favori épinglé depuis cette carte reste le même d'une semaine à l'autre. */}
          <Link href="/equipe/temps/planning" className="center-card center-card--week" aria-label="Planning de la semaine"
            {...menuLien('/equipe/temps/planning', 'Planning de la semaine', 'Ouvrir le planning')}>
            <span className="center-card__label"><CalendarDays size={15} aria-hidden="true" /> Cette semaine · du {frWeek(d.planning.week_start)}</span>
            {d.planning.shifts > 0 ? (
              <>
                <span className="center-card__big">{d.planning.shifts} shift{d.planning.shifts > 1 ? 's' : ''} · {d.planning.persons} salarié{d.planning.persons > 1 ? 's' : ''}</span>
                <span className="center-card__line">
                  {d.planning.tonight > 0 ? `${d.planning.tonight} shift${d.planning.tonight > 1 ? 's' : ''} aujourd’hui · ` : ''}
                  {d.planning.published_version ? `planning v${d.planning.published_version} publié` : 'brouillon — pas encore publié'}
                </span>
              </>
            ) : (
              <>
                <span className="center-card__big">Semaine vide</span>
                <span className="center-card__line">Ouvrez le planning pour poser les premiers shifts.</span>
              </>
            )}
            <span className="center-card__cta">Ouvrir le planning <ArrowRight size={14} aria-hidden="true" /></span>
          </Link>

          {/* ─── À TRAITER — uniquement des files réelles (§14) ─── */}
          <section className="center-card center-card--attn" aria-label="À traiter">
            <span className="center-card__label"><ClipboardList size={15} aria-hidden="true" /> À traiter</span>
            {d.attention.length > 0 ? (
              <div className="center-attn">
                {d.attention.map((a) => (
                  <button key={a.key} className="center-attn__row" onClick={() => navigate(a.route)}>
                    <span>{a.label}</span>
                    <span className="center-attn__count">{a.count}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="center-card__line" style={{ margin: 0 }}>Rien en attente — les dossiers sont à jour.</p>
            )}
          </section>

          {/* ─── ÉQUIPE ─── */}
          <Link href="/equipe/rh/salaries" className="center-card center-card--team" aria-label="Salariés"
            {...menuLien('/equipe/rh/salaries', 'Salariés', 'Voir les salariés')}>
            <span className="center-card__label"><Users size={15} aria-hidden="true" /> Équipe</span>
            <span className="center-card__big">{d.people.active} salarié{d.people.active > 1 ? 's' : ''} actif{d.people.active > 1 ? 's' : ''}</span>
            <span className="center-card__line">
              {[
                d.people.onboarding > 0 ? `${d.people.onboarding} arrivée${d.people.onboarding > 1 ? 's' : ''} en cours` : null,
                d.people.leaving > 0 ? `${d.people.leaving} départ${d.people.leaving > 1 ? 's' : ''}` : null,
              ].filter(Boolean).join(' · ') || 'Effectif stable.'}
            </span>
            <span className="center-card__cta">Voir les salariés <ArrowRight size={14} aria-hidden="true" /></span>
          </Link>

          {/* ─── ORGANISATION ─── */}
          <Link href="/equipe/organisation" className="center-card center-card--org" aria-label="Organisation"
            {...menuLien('/equipe/organisation', 'Organisation', 'Ouvrir l’organisation')}>
            <span className="center-card__label"><Building2 size={15} aria-hidden="true" /> Organisation</span>
            <span className="center-card__line" style={{ marginTop: 4 }}>
              {[
                `${d.org.teams} équipe${d.org.teams > 1 ? 's' : ''}`,
                `${d.org.positions} poste${d.org.positions > 1 ? 's' : ''}`,
                `${d.org.functions} fonction${d.org.functions > 1 ? 's' : ''}`,
                `${d.org.locations} lieu${d.org.locations > 1 ? 'x' : ''}`,
              ].join(' · ')}
            </span>
            <span className="center-card__cta">Ouvrir l’organisation <ArrowRight size={14} aria-hidden="true" /></span>
          </Link>
        </div>
      )}
    </main>
  )
}
